import { TableActions } from '@core/components/Table';
import { useApiUtils } from '@core/util/apiUtils';
import { permissaoUtils } from '@core/util/permissaoUtil';
import DeleteIcon from '@material-ui/icons/Delete';
import SpeedDialIcon from '@material-ui/lab/SpeedDialIcon';
import { vendaApi } from 'app/services/api/vendaApi';
import * as Actions from 'app/store/actions';
import { getCurrency } from 'app/utils/monetarioUtil';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import moment from 'moment';
import { default as React, useContext, useEffect, useState } from 'react';
import { FaFileExcel, FaFilePdf } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import * as XLSX from 'xlsx';
import { VendaContext } from '../../VendaContext';
import { VendaModais } from '../modal/VendaModais';

const VendaFab = ({ onDelete }) => {
  const [desabilitarExcluirVenda, setDesabilitarExcluirVenda] = useState(false);
  const [, setModal] = useContext(VendaContext).modal;
  const [search] = useContext(VendaContext).search;
  const [selecao] = useContext(VendaContext).selecao;
  const [tipoSelecao] = useContext(VendaContext).tipoSelecao;
  const [, setVendaAtual] = useContext(VendaContext).vendaAtual;
  const [filtroAvancado] = useContext(VendaContext).filtroAvancado;
  const [, setLoading] = useContext(VendaContext).loading;

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  const existeSelecionados = selecao.length > 0 || tipoSelecao !== 'NENHUM';

  useEffect(() => {
    setDesabilitarExcluirVenda(false);

    // Testa se pelo menos uma das vendas possui cobranca baixada, remessa gerada ou faturamento com nota fiscal
    selecao.forEach((s) => {
      if (s?.possuiCobrancaBaixadaOuRemessaGerada) {
        setDesabilitarExcluirVenda(true);
      }
    });
  }, [selecao]);

  const montarExcel = (filename, response) => {
    // Formatar os dados
    const formattedData = response.data.content.map((row) => ({
      'Data da Venda': row.data ? moment(row.data).format('DD/MM/YYYY') : '',
      Identificador: row.identificador?.toString().padStart(3, '0'),
      Cliente: row.nomeCliente,
      Valor: montarValor(row.valor),
      Situação: montarSituacao(row.situacao),
    }));

    const dataEmissao = [
      {
        'Data da Venda': `Emitido por Group Financeiro - ${moment().format('DD/MM/YYYY HH:mm:ss')}`,
        Identificador: '',
        Cliente: '',
        Valor: '',
        Situação: '',
      },
    ];

    // Inserir a linha de data de emissão antes dos dados
    const finalData = [...formattedData, [], ...dataEmissao];

    // Criar worksheet e workbook
    const worksheet = XLSX.utils.json_to_sheet(finalData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Vendas');

    // Exportar arquivo
    XLSX.writeFile(workbook, filename);
  };

  const exportToExcel = async (filename = `tabela_venda_${moment().format('DDMMYYYY_HHmmss')}.xlsx`) => {
    setLoading(true);

    let filtro = {
      page: 0,
      q: search,
      size: 10000,
      sort: 'data,DESC',
    };

    try {
      if (!filtro?.filtroAvancado) {
        filtro.filtroAvancado = filtroAvancado;
      }

      const response = await vendaApi().buscarTodos(filtro);

      montarExcel(filename, response);
    } catch (err) {
      apiUtils.handleApiError(err);
    }

    setLoading(false);
  };

  const montarPDF = (filename, response) => {
    const doc = new jsPDF();

    // Formatar os dados
    const tableData = response.data.content.map((row) => [
      row.data ? moment(row.data).format('DD/MM/YYYY') : '',
      row.identificador?.toString().padStart(3, '0'),
      row.nomeCliente,
      montarValor(row.valor),
      montarSituacao(row.situacao),
    ]);

    // Opções de rodapé
    const footer = () => {
      doc.setFontSize(10);
      doc.text(
        `Emitido por Group Financeiro - ${moment().format('DD/MM/YYYY HH:mm:ss')}`,
        doc.internal.pageSize.getWidth() - 20, // Alinha à direita
        doc.internal.pageSize.getHeight() - 10, // Posição vertical no rodapé
        { align: 'right' }
      );
    };

    // Adicionar tabela ao PDF
    doc.autoTable({
      head: [['Data da Venda', 'Identificador', 'Cliente', 'Valor', 'Situação']],
      body: tableData,
      didDrawPage: footer, // Chamado sempre que uma nova página é desenhada
    });

    // Salvar PDF
    doc.save(filename);
  };

  const exportToPDF = async (filename = `tabela_venda_${moment().format('DDMMYYYY_HHmmss')}.pdf`) => {
    setLoading(true);

    let filtro = {
      page: 0,
      q: search,
      size: 10000,
      sort: 'data,DESC',
    };

    try {
      if (!filtro?.filtroAvancado) {
        filtro.filtroAvancado = filtroAvancado;
      }

      const response = await vendaApi().buscarTodos(filtro);

      montarPDF(filename, response);
    } catch (err) {
      apiUtils.handleApiError(err);
    }

    setLoading(false);
  };

  const montarValor = (valor) => {
    return getCurrency(valor);
  };

  const montarSituacao = (situacao) => {
    if (situacao === 'VENDA_APROVADA') {
      return 'Venda aprovada';
    }

    if (situacao === 'EM_ORCAMENTO') {
      return 'Em orçamento';
    }

    if (situacao === 'ORCAMENTO_RECUSADO') {
      return 'Orçamento recusado';
    }
  };

  const triggerCreate = async (modal) => {
    let vendedor = {};
    const response = await vendaApi().getVendedorUsuario();

    if (response.data) {
      vendedor = {
        id: response.data?.id,
        pessoa: { nome: response.data?.nome },
      };
    }

    setVendaAtual({
      vendedor,
    });
    setModal(modal);
  };

  const triggerDelete = () => {
    onDelete({ ids: selecao, q: search, tipoSelecao, tipoFiltro: 'SIMPLES' });
  };

  const actions = [
    {
      icon: <DeleteIcon />,
      name: 'delete',
      tooltip: 'Excluir',
      show: permissaoUtils.venda().excluir && existeSelecionados,
      onClick: () => !desabilitarExcluirVenda && triggerDelete(),
      disabled: desabilitarExcluirVenda && 'Pelo menos uma das vendas selecionadas não pode ser excluída',
    },
    {
      icon: <SpeedDialIcon />,
      name: 'createOrcamentoVenda',
      tooltip: 'Cadastrar orçamento/venda',
      show: permissaoUtils.venda().cadastrar,
      onClick: () => triggerCreate(VendaModais.cadastrarVenda),
    },
    {
      icon: <FaFileExcel />,
      name: 'createOrcamentoVendaExcel',
      tooltip: 'Exportar tabela em Excel',
      show: permissaoUtils.venda().cadastrar,
      onClick: () => exportToExcel(),
    },
    {
      icon: <FaFilePdf />,
      name: 'createOrcamentoVendaPDF',
      tooltip: 'Exportar tabela em PDF',
      show: permissaoUtils.venda().cadastrar,
      onClick: () => exportToPDF(),
    },
  ];

  return <TableActions className={'stepFabButton'} actions={actions} />;
};

export default VendaFab;
